import * as Sentry from '@sentry/react';
import { cloneDeep } from 'lodash';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import { isAALViewOnlyActive } from 'bundles/course-staff-impersonation/utils/actAsLearnerSessionUtils';
import getLocalStorageProgressKey from 'bundles/in-course-stores/utils/getLocalStorageProgressKey';

type CourseProgressItem = {
  progressState: string;
  // Epoch milliseconds, as sent by onDemandCoursesProgress.v1.
  timestamp: number;
};

type CachedCourseProgressItem = {
  progressState: string;
  // ISO 8601, as written by saveProgressToLocalStorage. Absent for the pre-timestamp cache format.
  timestamp?: string;
};

type CourseProgress = {
  progressMapping: Record<string, { get: (key: string) => string }>;
};

type LocalStorageProgress = {
  [itemId: string]: CachedCourseProgressItem;
};

type RawProgressElement = {
  items: Record<string, CourseProgressItem>;
};

export type RawProgress = {
  paging: any;
  linked: any;
  elements: RawProgressElement[];
};

// localStorage is user-writable and may hold arbitrary or corrupted JSON, so a parsed value is
// only usable as a progress map once it is confirmed to be a plain object.
const isProgressMap = (value: unknown): value is LocalStorageProgress =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readProgressMap = (localStorageProgressKey: string): LocalStorageProgress => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(localStorageProgressKey) || '{}');
    return isProgressMap(parsed) ? parsed : {};
  } catch (error) {
    Sentry.captureException(error);
    return {};
  }
};

// Store in cache so if page is refreshed and the item completed event has not processed yet, it still shows as completed.
export const saveProgressToLocalStorage = (progressId: string, courseProgress: CourseProgress) => {
  if (window?.localStorage && !isAALViewOnlyActive()) {
    const localStorageProgressKey = getLocalStorageProgressKey(progressId);
    const itemsProgress = courseProgress.progressMapping;
    const localStorageProgress = readProgressMap(localStorageProgressKey);
    const currentTime = new Date().toISOString();

    // Remove items that are not in the new format (i.e., do not have a timestamp)
    Object.keys(localStorageProgress).forEach((itemId) => {
      if (!localStorageProgress[itemId]?.timestamp) {
        delete localStorageProgress[itemId];
      }
    });

    // Add or update items with the current progress state and timestamp
    Object.keys(itemsProgress).forEach((itemId) => {
      const itemProgressState = itemsProgress[itemId]?.get('progressState');
      if (!localStorageProgress[itemId] && itemProgressState === 'Completed') {
        localStorageProgress[itemId] = {
          progressState: itemProgressState,
          timestamp: currentTime,
        };
      }
    });

    localStorageEx.setItem(localStorageProgressKey, localStorageProgress, JSON.stringify);
  }
};

export const readProgressFromLocalStorage = (courseProgressId: string): LocalStorageProgress | undefined => {
  try {
    const storedValue = localStorageEx.getItem<unknown>(
      getLocalStorageProgressKey(courseProgressId),
      JSON.parse,
      undefined
    );
    return isProgressMap(storedValue) ? storedValue : undefined;
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
};

export const mergeProgressFromLocalStorage = (
  rawProgress: RawProgress,
  localStorageProgress?: LocalStorageProgress | null
): RawProgress => {
  if (
    !localStorageProgress ||
    !rawProgress?.elements ||
    rawProgress.elements.length === 0 ||
    !rawProgress.elements[0]
  ) {
    return rawProgress;
  }

  const mergedItemProgresses: Record<string, CourseProgressItem> = cloneDeep(rawProgress.elements[0].items);
  Object.keys(localStorageProgress).forEach((itemId) => {
    const localStorageItem = localStorageProgress[itemId];
    // Ignore old format items without timestamp
    if (localStorageItem?.timestamp) {
      if (localStorageItem.progressState === 'Completed') {
        mergedItemProgresses[itemId] = {
          progressState: localStorageItem.progressState,
          // Convert to the epoch milliseconds the server sends, so merged items are uniform.
          timestamp: new Date(localStorageItem.timestamp).getTime(),
        };
      }
    }
  });

  return {
    paging: rawProgress.paging,
    linked: rawProgress.linked,
    elements: [
      {
        ...rawProgress.elements[0],
        items: mergedItemProgresses,
      },
    ],
  };
};

const CACHE_EXPIRATION_TIME = 60 * 1000; // 1 minute
export const invalidateCache = (progressId: string) => {
  if (window?.localStorage) {
    const localStorageProgressKey = getLocalStorageProgressKey(progressId);
    const localStorageProgress = readProgressMap(localStorageProgressKey);
    const currentTime = new Date().getTime();
    Object.keys(localStorageProgress).forEach((itemId) => {
      const item = localStorageProgress[itemId];
      if (item?.timestamp) {
        const itemTime = new Date(item.timestamp).getTime();
        if (currentTime - itemTime > CACHE_EXPIRATION_TIME) {
          delete localStorageProgress[itemId];
        }
      } else {
        // Remove item if it doesn't have a timestamp (old format)
        delete localStorageProgress[itemId];
      }
    });
    localStorageEx.setItem(localStorageProgressKey, localStorageProgress, JSON.stringify);
  }
};
